<script setup lang="ts">
import type { WPCollection } from '@/types/wp';

import { date, euro } from '@/helpers';
import { loadGoogleMapsAPI } from '@/services/google';
import { storage } from '@/services/storage';
import { computed, onMounted, ref } from 'vue';
import { back, loadSvgMap, useFilters, WORDPRESS_COLLECTION_STORAGE_KEY } from '..';

import Loading from '@/components/Loading.vue';
import Maps from '@/components/Maps.vue';
import Ratings from '@/components/Ratings.vue';

const collection = defineModel<WPCollection>('collection', {
  required: true,
});

loadGoogleMapsAPI({ key: collection.value.google_api_key, v: 'weekly' });

const isExpanded = ref(false);

const { show, start, loading, success, searchParams, featured, getFeatured } = useFilters();

onMounted(async () => {
  storage.set(WORDPRESS_COLLECTION_STORAGE_KEY, { [searchParams.value!.get('accommodation_id')!.toString()]: collection.value });

  console.log('Show.vue mounted with collection:', collection.value);

  try {
    await show();
  } catch (error) {
    console.error(error);
  }
});

const included = computed(() => [
  ...start.value!.pricing.included.slice(0, 1),
  {
    title: 'Gratis eigen ski’s mee',
    text: 'Inclusief extra stuk ruimbagage om eigen wintersportuitrusting mee te nemen.',
    type: 'skis',
  },
  ...start.value!.pricing.included.slice(1, start.value!.pricing.included.length),
]);
</script>

<template>
  <Loading v-model:loading="loading" :fallback="success === false" @fallback="getFeatured">
    <template #fallback>
      <div class="loader__empty">
        <div class="--inner">
          <span class="icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="32" height="32">
              <path
                d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z"
              />
            </svg>
          </span>
          <h2 class="block__title">Deze accommodatie is niet meer beschikbaar</h2>
          <p class="block__text">Deze accommodatie is helaas niet meer beschikbaar. Probeer een andere zoekopdracht of vraag vrijblijvend een offerte aan via de chat.</p>
          <a class="button back" href="/wintersportreizen">Ga terug</a>
        </div>
      </div>
      <template v-if="featured">
        <div class="container">
          <div class="row m-auto">
            <div class="block__content">
              <div class="block__header mb-3">
                <h3 class="block__title mb-0">Bekijk ook één van onderstaande reisopties</h3>
              </div>
            </div>
          </div>
          <div class="row">
            <template v-for="(item, index) in featured" :key="index">
              <div class="col-md-6">
                <a class="block block--package" :href="item.pricing.accommodationurl" :title="item.room.accommodation.name" tarindex="_top">
                  <p class="block__img__meta">{{ date.format(new Date(item.date)) }}, {{ item.persons }} personen</p>
                  <img defer v-if="item.room.accommodation.photos[0]" :src="item.room.accommodation.photos[0].url" class="block__img" />
                  <div class="block__content">
                    <div class="block__header">
                      <h3 class="block__title">{{ item.room.accommodation.name }}</h3>
                      <Ratings :rating="item.room.rating" />
                    </div>
                    <ul>
                      <template v-for="({ title }, index) in item.pricing.included" :key="index">
                        <li>{{ title }}</li>
                      </template>
                      <li>Gratis eigen ski’s mee op de vlucht</li>
                    </ul>
                  </div>
                  <div class="block__footer">
                    <div class="block__price">
                      <span class="block__price">
                        {{ euro.format(item.pricing.price.perperson.amount) }}
                      </span>
                      <span class="block__price"> p.p. </span>
                    </div>
                    <a class="button" title="" :href="item.pricing.accommodationurl">Bekijk</a>
                  </div>
                </a>
              </div>
            </template>
          </div>
        </div>
      </template>
    </template>
    <section class="section">
      <div class="container">
        <a @click="back()" class="link back"> TERUG NAAR ZOEKRESULTATEN </a>
        <div class="block block--single">
          <div class="block__header">
            <h1 class="block__title">{{ start?.accommodation.name }}</h1>
            <Ratings :rating="start?.accommodation.rating!" class="block__rating" />
          </div>
          <template v-if="collection.usps">
            <ul class="block__usps">
              <li v-for="({ item }, index) in collection.usps" :key="index">
                <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
                  <path
                    d="M0.532265 6.73541H2.59063C3.17721 6.7346 3.65222 6.26509 3.65303 5.68529C3.65221 5.49311 3.75722 5.31634 3.92705 5.22388C4.09687 5.13063 4.30361 5.1355 4.4685 5.23767L7.53437 7.13274L5.65814 2.25188C5.5843 2.0605 5.62942 1.84317 5.77463 1.69641C5.91984 1.54882 6.13807 1.49855 6.33333 1.56747L7.89289 2.11078C8.36216 2.27701 8.88557 2.09537 9.14728 1.67613L10.0481 0.248133C10.1449 0.0940612 10.3163 0 10.5001 0C10.6839 0 10.8553 0.0940633 10.9521 0.248133L11.8529 1.67613C12.1146 2.09535 12.6372 2.27619 13.1073 2.11078L14.6669 1.56828H14.6661C14.8621 1.50017 15.0804 1.55044 15.2256 1.69722C15.3708 1.84399 15.4159 2.06131 15.3421 2.25269L13.4658 7.13355L16.5317 5.23848C16.6958 5.13712 16.9033 5.13144 17.0732 5.22469C17.2422 5.31795 17.3472 5.49473 17.3472 5.6861C17.348 6.2659 17.823 6.73622 18.4096 6.73703H20.4679C20.6739 6.73703 20.8609 6.8538 20.9487 7.03787C21.0365 7.22113 21.0102 7.43846 20.8806 7.59577L19.7936 8.90618V8.90699C19.6008 9.13891 19.5179 9.44056 19.5655 9.73736C19.6131 10.0333 19.7862 10.2953 20.0422 10.4574L20.0832 10.4834C20.235 10.5799 20.3277 10.7453 20.3285 10.9237C20.3293 11.1021 20.2391 11.2692 20.0881 11.3673L15.6309 14.2638L16.4037 15.5864C16.5087 15.7656 16.4972 15.9894 16.375 16.1572C16.2519 16.3259 16.0411 16.407 15.836 16.3664L11.0318 15.4031V19.4729C11.0318 19.764 10.793 20 10.4993 20C10.2048 20 9.96605 19.764 9.96605 19.4729V15.4014L5.16353 16.3648C4.96746 16.4037 4.76647 16.3323 4.64176 16.1783C4.51706 16.025 4.48916 15.815 4.57038 15.635L5.39652 13.7983L0.948294 11.3892V11.3884C0.783396 11.2992 0.677559 11.1305 0.671824 10.9448C0.665262 10.7591 0.758785 10.5832 0.917128 10.4834L0.958145 10.4575V10.4566C1.21412 10.2953 1.38722 10.0333 1.43479 9.73656C1.48319 9.44057 1.40033 9.13811 1.20754 8.90619L0.120523 7.59578C-0.00991821 7.43765 -0.0369911 7.22033 0.0516129 7.03626C0.139395 6.85219 0.327263 6.73461 0.533188 6.73461L0.532265 6.73541Z"
                    fill="#CA2D31"
                  />
                </svg>
                {{ item }}
              </li>
            </ul>
          </template>
          <div class="block__images">
            <template v-if="collection.thumbnail">
              <img :src="collection.thumbnail" class="block__img" />
            </template>
            <img
              v-for="(photo, index) in collection.thumbnail ? collection.photos : collection.photos.slice(1)"
              :src="photo"
              :key="index"
              class="block__img"
            />
          </div>
        </div>
        <div class="row">
          <div class="col-lg-7">
            <div class="block block--read-more margin-big">
              <div class="block__content">
                <span v-html="isExpanded ? collection.description : collection.excerpt"></span>
              </div>
              <a class="link" href="#target" @click.prevent="isExpanded = !isExpanded">
                {{ isExpanded ? 'LEES MINDER' : 'LEES MEER' }}
              </a>
            </div>
            <div class="block block--inclusive margin-big">
              <h4 class="block__title">Inclusief bij deze reis</h4>
              <ul class="block__items">
                <li v-for="item in included" :key="item.title">
                  <img :src="loadSvgMap[item.type]" />
                  <span>
                    <p>{{ item.title }}</p>
                    <p>{{ item.text }}</p>
                  </span>
                </li>
              </ul>
            </div>
            <template v-if="collection.pistes">
              <div class="block block--pistes margin-big">
                <h4 class="block__title">Over de pistes</h4>
                <ul class="block__items">
                  <li v-for="item in collection.pistes" :key="item.name">
                    <span class="text">{{ item.name }}</span>
                    <span class="chart">
                      <span class="progress" :style="{ width: item.percentage + '%', background: item.color }"></span>
                    </span>
                    <span class="number">{{ item.percentage }}%</span>
                  </li>
                </ul>
              </div>
            </template>
            <div id="target" v-if="collection.content" class="block margin-big">
              <span v-html="collection.content"></span>
            </div>
            <div class="block margin-big">
              <h4 class="block__title">Locatie op de kaart</h4>
              <Maps
                :location="{
                  latitude: start?.accommodation.latitude,
                  longitude: start?.accommodation.longitude,
                }"
              />
            </div>
          </div>
          <div class="col-lg-4 offset-lg-1">
            <div class="block block--sidebar">
              <h4 class="block__title">Jouw reis</h4>
              <ul class="block__list">
                <li>
                  <span>Vertrekdatum</span
                  ><span>{{ start?.pricing.startdate ? date.format(new Date(start?.pricing.startdate)) : '' }}</span>
                </li>
                <li>
                  <span>Reisduur</span><span>{{ start?.pricing.duration }} nachten</span>
                </li>
                <li>
                  <span>Aantal personen</span
                  ><span>
                    {{ Number(start?.pricing?.persons?.adults ?? 0) + Number(start?.pricing?.persons?.children ?? 0) }}
                    personen</span
                  >
                </li>
              </ul>
              <hr />
              <div class="block__price">
                <span>Prijs voor deze reis</span>
                <span>{{ euro.format(start?.pricing.price.perperson.amount ?? 0) }} p.p.</span>
              </div>
              <a :href="start?.pricing.bookurl" class="button block"> Boek deze reis </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  </Loading>
</template>

<style scoped>
.block__rating {
  margin: 0 0 0 15px;
}
</style>
